import {
	StyledPhotoEditorContainer,
	StyledPhotoEditorFramed,
	StyledPhotoEditorFramedContent,
	StyledPhotoEditorLoadingSpinner,
	StyledPhotoEditorFramedSpinnerContent
} from 'components/Selfiebooth/PhotoEditor/styled'
import Controls from 'components/Selfiebooth/PhotoEditor/controls'
import Stickers from 'components/Selfiebooth/PhotoEditor/stickers'
import PhotoCamera from 'components/Selfiebooth/PhotoEditor/photoCamera'
import useStickers from './../hooks/useStickers'
import usePhotoEditor from './../hooks/usePhotoEditor'
import { useEffect, useRef, useState } from 'react'
import { DEFAULT_STICKERS } from 'components/Selfiebooth/utils/consts'
import PhotoUpload from 'components/Selfiebooth/PhotoEditor/photoUpload'
import { SelfieBoothType, WatermarkSelfieBoothType } from '../../../types/selfiebooth'

interface PhotoEditorProps {
	selfiebooth: SelfieBoothType;
	photoUrl: string;
	isCapturing: boolean;
	onRetake: () => void;
	setFinalImg: (img: string) => void;
	method: 'camera'|'upload';
	setPhotoUrl: (url: string) => void;
	isModal: boolean;
}

const PhotoEditor = ({ selfiebooth, photoUrl, isCapturing, onRetake, setFinalImg, method, setPhotoUrl, isModal }: PhotoEditorProps) => {
	const editorRef = useRef<HTMLDivElement | null>(null);
	const [imageRotation, setImageRotation] = useState<number>(0);
	const { part: infos } = selfiebooth;

	const watermark :WatermarkSelfieBoothType = {
		isUse : !infos.noWatermark,
		size : infos.watermarkSize,
		url : infos.watermarkUrl,
		position : infos.watermarkPosition,
		opacity : infos.watermarkOpacity,
	}
	if(watermark.isUse && watermark.url === '') {
		watermark.url = '/static/photobooth/msj/msj.svg';
	}

	const { addStickers, stickers, selectedStickerId, updateSticker, deleteSticker, selectSticker } = useStickers();
	const { savePhotoWithStickers, isSaving, linkImg } = usePhotoEditor();

	useEffect(() => {
		if(linkImg) {
			setFinalImg(linkImg)
		}
	}, [linkImg])

	useEffect(() => {
		if (method === 'upload') {
			const img = new Image();
			img.src = photoUrl;
		}
	}, [method, photoUrl]);

	const handleSave = async () => {
		if(!editorRef.current) return;

		const { offsetWidth, offsetHeight } = editorRef.current

		await savePhotoWithStickers(
			photoUrl,
			stickers,
			offsetWidth,
			offsetHeight,
			imageRotation,
			watermark,
			method
		);
	};

	const handleRotateImage = () => {
		setImageRotation(prev => (prev + 90) % 360);
	};

	if(isCapturing) {
		return <>
			<StyledPhotoEditorContainer $isModal={isModal}>
				<StyledPhotoEditorFramed>
					<StyledPhotoEditorFramedSpinnerContent>
						<StyledPhotoEditorLoadingSpinner />
					</StyledPhotoEditorFramedSpinnerContent>
				</StyledPhotoEditorFramed>
			</StyledPhotoEditorContainer>
		</>
	}

	return <>
		<StyledPhotoEditorContainer $isModal={isModal}>
			<StyledPhotoEditorFramed>
				<StyledPhotoEditorFramedContent>
					<Controls
						returnCamera={onRetake}
						reversePhoto={handleRotateImage}
						validPhoto={handleSave}
					/>
					{infos.useStickers !== 'none' &&
						<Stickers
							stickers={infos.useStickers === 'default' ? DEFAULT_STICKERS : infos.stickers}
							addStickers={addStickers}
						/>
					}
					{method === 'upload' ? <>
						<PhotoUpload
							photoUrl={photoUrl}
							stickers={stickers}
							watermark={watermark}
							selectedStickerId={selectedStickerId}
							updateSticker={updateSticker}
							deleteSticker={deleteSticker}
							selectSticker={selectSticker}
							ref={editorRef}
							imageRotation={imageRotation}
							setPhotoUrl={setPhotoUrl}
						/>
					</> : <>
						<PhotoCamera
							photoUrl={photoUrl}
							stickers={stickers}
							watermark={watermark}
							selectedStickerId={selectedStickerId}
							updateSticker={updateSticker}
							deleteSticker={deleteSticker}
							selectSticker={selectSticker}
							ref={editorRef}
							imageRotation={imageRotation}
							isModal={isModal}
						/>
					</>}
				</StyledPhotoEditorFramedContent>
			</StyledPhotoEditorFramed>
		</StyledPhotoEditorContainer>
	</>;
}

export default PhotoEditor;